import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Snackbar, Alert } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import PageHeader from '../reusable/PageHeader';
import SelectDropdown from '../reusable/SelectDropdown';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import {
  getChatConfig,
  updateChatConfig,
  ChatConfig,
  ChatConfigSection,
  ChatModelOption,
} from '../../services/chatConfigService';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './ChatConfigPage.css';

interface ToastState {
  open: boolean;
  message: string;
}

const ChatConfigPage = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [modelId, setModelId] = useState('');
  const [temperature, setTemperature] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [sections, setSections] = useState<ChatConfigSection[]>([]);
  const [availableModels, setAvailableModels] = useState<ChatModelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '' });

  const applyConfig = useCallback((config: ChatConfig) => {
    setModelId(config.model_id ?? '');
    setTemperature(config.temperature == null ? '' : String(config.temperature));
    setMaxTokens(config.max_tokens == null ? '' : String(config.max_tokens));
    setSections(config.prompt_sections);
    setAvailableModels(config.available_models ?? []);
  }, []);

  const temperatureSupported = availableModels.find((model) => model.id === modelId)?.supports_temperature ?? true;

  const fetchConfig = useCallback(async () => {
    if (!userDetails) return;
    try {
      setLoading(true);
      const response = await getChatConfig(userDetails.access_token);
      applyConfig(response.data);
    } catch (error) {
      handleOpenErrorModal('Error fetching chat configuration', error as Error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, handleOpenErrorModal, applyConfig]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSectionChange = (key: string, content: string) => {
    setSections((prev) => prev.map((section) => (section.key === key ? { ...section, content } : section)));
  };

  const handleSave = async () => {
    if (!userDetails) return;
    setSaving(true);
    try {
      const response = await updateChatConfig(userDetails.access_token, {
        model_id: modelId.trim() === '' ? null : modelId.trim(),
        temperature: temperature.trim() === '' ? null : parseFloat(temperature),
        max_tokens: maxTokens.trim() === '' ? null : parseInt(maxTokens, 10),
        prompt_sections: sections
          .filter((section) => section.editable)
          .map((section) => ({ key: section.key, content: section.content })),
      });
      applyConfig(response.data);
      setToast({ open: true, message: 'Chat configuration saved.' });
    } catch (error) {
      handleOpenErrorModal('Error saving chat configuration', error as Error);
    } finally {
      setSaving(false);
    }
  };

  if (!userDetails || loading) return <Loader />;

  const saveButton = <CustomButton variant="primary" buttonText="Save" buttonAction={handleSave} loading={saving} />;

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <PageHeader
        title="Chat Configuration"
        pageDescription="Configure the model and system prompt used by the chatbot. Read-only sections are shown for reference."
      />

      <div className="chat-config-actions chat-config-actions-top">{saveButton}</div>

      <Panel className="chat-config-panel">
        <h2 className="chat-config-section-title">Model</h2>
        <div className="chat-config-fields">
          <SelectDropdown
            label="Model"
            selectedValue={modelId}
            handleChange={(e) => setModelId(e.target.value)}
            items={availableModels.map((model) => ({ label: model.label, value: model.id }))}
            isClearable={false}
            formControlVariant="outlined"
          />
          <TextField
            label="Temperature"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            disabled={!temperatureSupported}
            helperText={temperatureSupported ? undefined : 'This model does not support temperature.'}
          />
          <TextField
            label="Max tokens"
            value={maxTokens}
            onChange={(e) => setMaxTokens(e.target.value)}
            type="number"
            inputProps={{ min: 1, max: 200000, step: 1 }}
          />
        </div>
      </Panel>

      <Panel className="chat-config-panel">
        <h2 className="chat-config-section-title">System prompt</h2>
        {sections.map((section) => (
          <div key={section.key} className="chat-config-prompt-section">
            <TextField
              label={section.editable ? section.title : `${section.title} (read-only)`}
              value={section.content}
              onChange={(e) => handleSectionChange(section.key, e.target.value)}
              disabled={!section.editable}
              multiline
              minRows={3}
              fullWidth
            />
          </div>
        ))}
      </Panel>

      <div className="chat-config-actions">{saveButton}</div>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ChatConfigPage;
